import { Link, useLocation } from 'react-router-dom';

import { DOCS_NAV } from '../../data/docs';

type DocsLayoutProps = {
    children: React.ReactNode;
    title: string;
    /** One short supporting sentence under the title. */
    lead?: string;
};

/**
 * Shared chrome for public API docs. Not the legal layout, docs need a
 * side-of-page index of endpoints, not a "last updated" stamp.
 */
export function DocsLayout({ children, title, lead }: DocsLayoutProps) {
    const { pathname } = useLocation();

    return (
        <div className="space-y-8 pb-16">
            <header className="space-y-3">
                <p className="eyebrow text-text-muted">API docs</p>
                <h1 className="text-title text-text">{title}</h1>
                {lead ? <p className="max-w-xl text-base leading-relaxed text-text-muted">{lead}</p> : null}
            </header>

            <nav aria-label="Documentation" className="flex flex-wrap gap-2 border-b border-border pb-4">
                {DOCS_NAV.map((item) => {
                    const active =
                        item.to === '/docs' ? pathname === '/docs' : pathname === item.to || pathname.startsWith(`${item.to}/`);
                    return (
                        <Link
                            key={item.to}
                            to={item.to}
                            className={`rounded-md border px-3 py-1.5 text-sm transition-colors ${
                                active
                                    ? 'border-secondary bg-secondary text-bg'
                                    : 'border-border-strong text-text-muted hover:border-secondary hover:text-text'
                            }`}
                        >
                            {item.label}
                        </Link>
                    );
                })}
            </nav>

            <div className="space-y-10">{children}</div>
        </div>
    );
}

export function DocsSection({
    title,
    children,
    id,
}: {
    title: string;
    children: React.ReactNode;
    id?: string;
}) {
    return (
        <section id={id} className="space-y-3">
            <h2 className="text-lg font-bold tracking-[-0.02em] text-text">{title}</h2>
            <div className="space-y-3 text-sm leading-relaxed text-text-muted">{children}</div>
        </section>
    );
}
